export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

/**
 * Formatea un precio con el símbolo de moneda según el tipoMoneda
 * @param amount - El monto a formatear
 * @param simbolo - El símbolo de la moneda (ej: "$", "USD", "€")
 * @returns String formateado con el símbolo delante del precio
 */
export const formatCurrencyWithSymbol = (amount: number, simbolo?: string | null): string => {
    if (!simbolo) {
        // Si no hay símbolo, usar formato ARS por defecto
        return formatCurrency(amount);
    }
    
    // Formatear el número con separador de miles y 2 decimales
    const formattedAmount = new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
    
    // Colocar el símbolo delante del precio
    return `${simbolo}${formattedAmount}`;
};

/**
 * Convierte un string de fecha (YYYY-MM-DD) a un objeto Date en la zona horaria local
 * Evita el problema de que new Date("YYYY-MM-DD") se interprete como UTC
 * @param dateString - String en formato YYYY-MM-DD
 * @returns Date en zona horaria local
 */
export const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    // month - 1 porque los meses en Date van de 0 a 11
    return new Date(year, month - 1, day);
};

/**
 * Fecha local como YYYY-MM-DD (p. ej. para defaults de input type="date").
 * Evita usar toISOString(), que usa UTC y puede cambiar el día calendario.
 */
export const toLocalDateStringYMD = (d: Date = new Date()): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/**
 * DD/MM/YYYY a partir de Date o string ISO/fecha del API, usando calendario local.
 * Para strings solo-fecha (YYYY-MM-DD) usa parseLocalDate (evita desfase UTC).
 */
export const formatDateLocal = (value: Date | string | null | undefined): string => {
    if (value == null || value === '') return '';
    let date: Date;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            date = parseLocalDate(trimmed);
        } else {
            date = new Date(value);
        }
    } else {
        date = value;
    }
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Formatea una fecha en formato DD/MM/YYYY
 * @param dateString - String de fecha (ISO o formato fecha)
 * @returns String formateado en DD/MM/YYYY
 */
export const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
        const trimmed = dateString.trim();
        const date = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
            ? parseLocalDate(trimmed)
            : new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (error) {
        return '';
    }
};

/**
 * Formatea una fecha con hora en formato DD/MM/YYYY HH:mm
 * @param dateString - String de fecha (ISO o formato fecha)
 * @returns String formateado en DD/MM/YYYY HH:mm
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        return '';
    }
};

/**
 * Notas por renglón (p. ej. columnas CHAR(n) en SQL): el API puede devolver espacios de relleno al final.
 * Se recorta con trimEnd para la UI y para no enviar cadenas de 500 caracteres vacíos al guardar.
 */
export function normalizeItemNotas(value: string | undefined | null, maxLen = 500): string {
    return String(value ?? '')
        .trimEnd()
        .slice(0, maxLen);
}